import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { MediaViewService } from '../media-view.service';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import moment from 'moment-timezone';
import { DATE_TIME_FORMATS } from '@fc-shared/utils/date-time-formats';

@Component({
  selector: 'fc-media-viewer-details',
  template: `
    <div class="flex flex-col">
      <div
        class="flex flex-row items-center justify-between border-b border-grey-100 px-24 py-16"
      >
        <div class="text-lg font-medium">Details</div>
        <ng-button
          variant="icon"
          icon="icon-close"
          (click)="closeInfo.emit()"
        ></ng-button>
      </div>

      <div class="flex-1 overflow-auto p-20">
        <div class="flex flex-col gap-16">
          @if (file()?.name) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-image"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ file()?.name }}</span>
            </div>
          }

          @if (getFormattedDate()) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-calendar_month"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ getFormattedDate() }}</span>
            </div>
          }

          @if (file()?.exif?.camera?.make) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-info-circle"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ file()?.exif?.camera?.make }}</span>
            </div>
          }

          @if (file()?.uploadedBy) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-person"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ file()?.uploadedBy }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col bg-white shadow-lg rounded-lg',
    '[style.height]': 'panelHeight',
    '[style.margin-bottom.px]': 'margin',
    '[style.margin-right.px]': 'margin',
    '[style.align-self]': "'flex-start'",
  },
  imports: [NgButtonComponent, IconComponent],
})
export class MediaViewerDetailsComponent {
  file = input<AttachmentModel>();
  closeInfo = output<void>();
  mediaViewService = inject(MediaViewService);

  readonly margin = 30;
  readonly panelHeight = `calc(100% - ${this.margin}px)`;

  getFormattedDate(): string | null {
    const file = this.file();
    if (!file) return null;

    // Try exif timestamp first, then created, then modified
    const dateString = file.exif?.timestamp || file.created || file.modified;
    if (!dateString) return null;

    return moment(dateString).format(DATE_TIME_FORMATS.displayTimestamp);
  }
}
